import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  onSnapshot,
  Timestamp,
} from "firebase/firestore";
import { User } from "firebase/auth";

export interface Project {
  id: string;
  name: string;
  description: string;
  userId: string;
  createdAt: Date;
  lastModified: Date;
}

export interface ProjectInput {
  name: string;
  description: string;
}

export class ProjectService {
  private db: any;
  private user: User | null;

  constructor(db: any, user: User | null) {
    this.db = db;
    this.user = user;
  }

  private validateUser(): void {
    if (!this.user) {
      throw new Error("Usuario no autenticado");
    }
  }

  private mapProjectData(doc: any): Project {
    const data = doc.data();
    return {
      id: doc.id,
      name: data.name,
      description: data.description,
      userId: data.userId,
      createdAt: data.createdAt?.toDate() || new Date(),
      lastModified: data.lastModified?.toDate() || new Date(),
    };
  }

  async createProject(projectData: ProjectInput): Promise<Project> {
    this.validateUser();

    const docRef = await addDoc(collection(this.db, "projects"), {
      ...projectData,
      userId: this.user!.uid,
      createdAt: serverTimestamp(),
      lastModified: serverTimestamp(),
    });

    const newDoc = await getDoc(docRef);
    return this.mapProjectData(newDoc);
  }

  async getUserProjects(): Promise<Project[]> {
    this.validateUser();

    const q = query(
      collection(this.db, "projects"),
      where("userId", "==", this.user!.uid),
      orderBy("lastModified", "desc")
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => this.mapProjectData(doc));
  }

  async getProject(projectId: string): Promise<Project | null> {
    this.validateUser();

    const docRef = doc(this.db, "projects", projectId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    const project = this.mapProjectData(docSnap);

    // Verificar que el proyecto pertenece al usuario
    if (project.userId !== this.user!.uid) {
      throw new Error("No tienes permisos para acceder a este proyecto");
    }

    return project;
  }

  async updateProject(
    projectId: string,
    updates: Partial<ProjectInput>
  ): Promise<void> {
    this.validateUser();

    // Verificar que el proyecto pertenece al usuario
    const project = await this.getProject(projectId);
    if (!project) {
      throw new Error("Proyecto no encontrado");
    }

    const docRef = doc(this.db, "projects", projectId);
    await updateDoc(docRef, {
      ...updates,
      lastModified: serverTimestamp(),
    });
  }

  async deleteProject(projectId: string): Promise<void> {
    this.validateUser();

    // Verificar que el proyecto pertenece al usuario
    const project = await this.getProject(projectId);
    if (!project) {
      throw new Error("Proyecto no encontrado");
    }

    const docRef = doc(this.db, "projects", projectId);
    await deleteDoc(docRef);
  }

  subscribeToUserProjects(callback: (projects: Project[]) => void): () => void {
    this.validateUser();

    const q = query(
      collection(this.db, "projects"),
      where("userId", "==", this.user!.uid),
      orderBy("lastModified", "desc")
    );

    return onSnapshot(q, (querySnapshot) => {
      const projects = querySnapshot.docs.map((doc) =>
        this.mapProjectData(doc)
      );
      callback(projects);
    });
  }
}
